import { useState, useEffect, FormEvent } from 'react';
import { useLocation } from 'wouter';
import { useAuthStore } from '../hooks/useAuth';
import { api } from '../lib/api';
import toast from 'react-hot-toast';
import { Loader2, Save, RotateCcw, Building2, MapPin, Settings as SettingsIcon, AlertCircle, Plus, AlertTriangle } from 'lucide-react';
import { FormField } from '../components/ui/FormField';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { WarehouseScheduleSettings } from '../components/settings/WarehouseScheduleSettings';
import { validatePhone, validatePostalCode } from '../utils/fieldValidation';

// Supported IANA timezones for warehouse configuration
const SUPPORTED_TIMEZONES = [
    { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
    { value: 'America/Anchorage', label: 'Alaska (AKST)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Vancouver', label: 'Pacific Time (PT) – Vancouver' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Edmonton', label: 'Mountain Time (MT) – Edmonton' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Winnipeg', label: 'Central Time (CT) – Winnipeg' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Toronto', label: 'Eastern Time (ET) – Toronto' },
    { value: 'America/Halifax', label: 'Atlantic Time (AT)' },
    { value: 'UTC', label: 'UTC' },
] as const;

// Allowed countries for warehouse address
const ALLOWED_COUNTRIES = [
    { value: 'USA', label: 'United States' },
    { value: 'Canada', label: 'Canada' },
    { value: 'Mexico', label: 'Mexico' },
] as const;

const CANADIAN_TIMEZONES = new Set([
    'America/Vancouver',
    'America/Edmonton',
    'America/Winnipeg',
    'America/Toronto',
    'America/Halifax',
    'America/St_Johns',
    'America/Regina',
]);

const CANADIAN_PROVINCES = [
    { value: 'AB', label: 'AB – Alberta' },
    { value: 'BC', label: 'BC – British Columbia' },
    { value: 'MB', label: 'MB – Manitoba' },
    { value: 'NB', label: 'NB – New Brunswick' },
    { value: 'NL', label: 'NL – Newfoundland and Labrador' },
    { value: 'NS', label: 'NS – Nova Scotia' },
    { value: 'NT', label: 'NT – Northwest Territories' },
    { value: 'NU', label: 'NU – Nunavut' },
    { value: 'ON', label: 'ON – Ontario' },
    { value: 'PE', label: 'PE – Prince Edward Island' },
    { value: 'QC', label: 'QC – Quebec' },
    { value: 'SK', label: 'SK – Saskatchewan' },
    { value: 'YT', label: 'YT – Yukon' },
] as const;

const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME',
    'MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI',
    'SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
] as const;

/** Detect the browser's IANA timezone; fall back to America/Los_Angeles if unsupported */
function detectBrowserTZ(): string {
    try {
        const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (SUPPORTED_TIMEZONES.some(tz => tz.value === detected)) return detected;
        return 'America/Los_Angeles';
    } catch { return 'America/Los_Angeles'; }
}

function detectDefaultCountry(): string {
    try {
        const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (CANADIAN_TIMEZONES.has(detected)) return 'Canada';
    } catch { /* ignore */ }
    return 'USA';
}

export function WarehouseOnboarding() {
    const { user, updateUser, login } = useAuthStore();
    const [, setLocation] = useLocation();

    const [mode, setMode] = useState<'create' | 'edit'>('create');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [warehouseId, setWarehouseId] = useState<string | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [isEmptyState, setIsEmptyState] = useState(false);
    const [warehouseSchedule, setWarehouseSchedule] = useState<{
        timeZone?: string; operatingDays?: number[]; openTime?: string; closeTime?: string; slotMinutes?: number; detentionGraceMinutes?: number; detentionRate?: number;
    }>({});

    // Initial Name State for Locking Logic
    const [initialName, setInitialName] = useState<string | null>(null);
    const [initialBookingId, setInitialBookingId] = useState<string | null>(null);
    const [showNameConfirm, setShowNameConfirm] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        publicBookingId: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: detectDefaultCountry(),
        phone: '',
        email: '',
        latitude: '',
        longitude: '',
        dockCount: 0,
        graceMinutes: 15,
        timeZone: detectBrowserTZ(),
    });

    // Strategy: Fetch logic
    async function loadWarehouseData() {
        setLoading(true);
        setLoadError(null);
        try {
            let targetId = user?.warehouseId;

            // If no ID on user, check if they have any warehouses
            if (!targetId) {
                const warehouses = await api.safeArrayRequest<any>('/warehouses');
                if (warehouses.length > 0) {
                    targetId = warehouses[0].id;
                } else {
                    // GENUINE EMPTY STATE
                    setIsEmptyState(true);
                    setMode('create');
                    setLoading(false);
                    return;
                }
            }

            if (targetId) {
                setWarehouseId(targetId);
                setMode('edit');
                setIsEmptyState(false);
                const data = await api.getWarehouse(targetId);
                if (data && data.warehouse) {
                    setInitialName(data.warehouse.name || null);
                    setInitialBookingId(data.warehouse.publicBookingId || null);
                    setFormData({
                        name: data.warehouse.name || '',
                        publicBookingId: data.warehouse.publicBookingId || '',
                        address: data.warehouse.address || '',
                        city: data.warehouse.city || '',
                        state: data.warehouse.state || '',
                        zipCode: data.warehouse.zipCode || '',
                        country: data.warehouse.country || 'USA',
                        phone: data.warehouse.phone || '',
                        email: data.warehouse.email || '',
                        latitude: data.warehouse.latitude || '',
                        longitude: data.warehouse.longitude || '',
                        dockCount: data.warehouse.dockCount || 0,
                        graceMinutes: data.warehouse.graceMinutes || 15,
                        timeZone: data.warehouse.timeZone || 'America/Los_Angeles',
                    });
                    setWarehouseSchedule({
                        timeZone: data.warehouse.timeZone || 'America/Los_Angeles',
                        operatingDays: data.warehouse.operatingDays ?? [1, 2, 3, 4, 5],
                        openTime: data.warehouse.openTime ?? '08:00',
                        closeTime: data.warehouse.closeTime ?? '17:00',
                        slotMinutes: data.warehouse.slotMinutes ?? 60,
                        detentionGraceMinutes: data.warehouse.detentionGraceMinutes ?? 60,
                        detentionRate: data.warehouse.detentionRate ?? 75,
                    });
                }
            }
        } catch (err) {
            console.error('Failed to load warehouse data', err);
            setLoadError('Failed to load warehouse profile. Please check your connection.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (user) {
            loadWarehouseData();
        }
    }, [user]);

    const handleChange = (field: string, value: string) => {
        // Auto-generate booking ID from name if not locked and no manual override (conceptually)
        // Actually, let's just do it simple: if modifying name, and booking ID is not locked (initial is null),
        // and user hasn't typed in booking ID manually yet (maybe tricky to track), 
        // simpler: if booking ID is empty or matches slug of OLD name, update it.
        // Simplest: just update it if it's not locked.

        let updates: any = { [field]: value };

        if (field === 'dockCount' || field === 'graceMinutes') {
            updates[field] = value === '' ? 0 : Math.max(0, parseInt(value) || 0);
        }

        if (field === 'timeZone') {
            setWarehouseSchedule(prev => ({ ...prev, timeZone: value }));
        }

        // Slugify Logic
        if (field === 'name' && !initialBookingId) {
            // Only auto-generate if not strictly locked
            const slug = value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
            // We only update publicBookingId if the user hasn't likely manually edited it to something completely different?
            // For now, let's just force sync it for better UX unless they explicitly edit the ID field.
            // But we can't easily tell if they edited ID field.
            // Let's just update it. User can override by editing ID field *after* typing name.
            updates['publicBookingId'] = slug;
        }

        setFormData(prev => ({
            ...prev,
            ...updates
        }));
    };

    const handleReset = async () => {
        if (mode === 'create') {
            setFormData({
                name: '',
                publicBookingId: '',
                address: '',
                city: '',
                state: '',
                zipCode: '',
                country: 'USA',
                phone: '',
                email: '',
                latitude: '',
                longitude: '',
                dockCount: 0,
                graceMinutes: 15,
                timeZone: detectBrowserTZ(),
            });
            return;
        }
        await loadWarehouseData();
        toast.success('Changes discarded');
    };

    // Pre-submit Check
    const handlePreSubmit = (e: FormEvent) => {
        e.preventDefault();

        // Field-level validation
        const errors: Record<string, string> = {};
        const phoneErr = validatePhone(formData.phone);
        if (phoneErr) errors.phone = phoneErr;
        if (!ALLOWED_COUNTRIES.some(c => c.value === formData.country)) {
            errors.country = 'Please select a valid country';
        }
        const postalErr = validatePostalCode(formData.zipCode, formData.country);
        if (postalErr) errors.zipCode = postalErr;
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            toast.error('Please fix the highlighted fields');
            return;
        }
        setFieldErrors({});

        // If name is being set for the first time
        if (!initialName && formData.name.trim().length > 0) {
            setShowNameConfirm(true);
            return;
        }
        executeSubmit();
    };

    const executeSubmit = async () => {
        setSaving(true);
        setShowNameConfirm(false);

        const payload = {
            ...formData,
            phone: formData.phone.trim(),
            // Ensure booking ID is set if empty (fallback to slug of name)
            publicBookingId: formData.publicBookingId || formData.name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-'),
        };

        try {
            if (mode === 'create') {
                const res = await api.createWarehouse(payload) as any;
                toast.success('Warehouse created successfully');
                if (res.warehouse?.id) {
                    setWarehouseId(res.warehouse.id);
                    setMode('edit');
                    setInitialName(res.warehouse.name);
                    setInitialBookingId(res.warehouse.publicBookingId);
                    setIsEmptyState(false);

                    if (user) {
                        const updatedUser = {
                            ...user,
                            warehouseId: res.warehouse.id,
                            isOnboarded: true,
                            plan: res.warehouse.plan ?? 'free_trial',
                            subscriptionStatus: res.warehouse.subscriptionStatus ?? 'trialing',
                            trialStatus: res.warehouse.subscriptionStatus ?? 'trialing',
                            trialEndAt: res.warehouse.trialEndAt ?? user.trialEndAt ?? null,
                            effectiveTier: res.warehouse.effectiveTier ?? 'operations',
                        };
                        if (res.token) {
                            login(updatedUser, res.token);
                        } else {
                            updateUser(updatedUser);
                        }
                    }

                    // Immediately progress the user to the next required onboarding step
                    const fromOnboarding = new URLSearchParams(window.location.search).get('from') === 'onboarding';
                    setLocation(fromOnboarding ? '/docks?from=onboarding' : '/docks');
                }
            } else if (mode === 'edit' && warehouseId) {
                // Security check for client-side override
                if (initialName && payload.name !== initialName) {
                    toast.error("Warehouse Name is locked.");
                    setLoading(false);
                    return;
                }
                if (initialBookingId && payload.publicBookingId !== initialBookingId) {
                    toast.error("Warehouse ID is locked.");
                    setLoading(false);
                    return;
                }

                await api.updateWarehouse(warehouseId, payload);
                toast.success('Warehouse profile updated');

                if (!initialName) setInitialName(payload.name);
                if (!initialBookingId) setInitialBookingId(payload.publicBookingId);
            }
        } catch (err: any) {
            console.error(err);
            toast.error(err.message || 'Failed to save changes');
        } finally {
            setSaving(false);
        }
    };

    // --- RENDER STATES ---

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                    <span className="text-sm text-muted-foreground">Loading profile...</span>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/50 rounded-lg p-6 max-w-md text-center">
                    <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-red-900 dark:text-red-200 mb-2">Error Loading Profile</h3>
                    <p className="text-sm text-red-700 dark:text-red-300 mb-4">{loadError}</p>
                    <button
                        onClick={loadWarehouseData}
                        className="btn btn-outline"
                    >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // EMPTY STATE VIEW
    if (isEmptyState) {
        return (
            <div className="p-6 max-w-5xl mx-auto space-y-8 animate-fade-in pb-20">
                <div className="flex flex-col items-center justify-center min-h-[400px] text-center space-y-4">
                    <div className="p-4 bg-primary/10 rounded-full text-primary mb-2">
                        <Building2 size={48} />
                    </div>
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">Welcome to BayWhiz</h1>
                    <p className="text-muted-foreground max-w-md">
                        You don't have any facilities set up yet. Create your first warehouse profile to start managing docks and appointments.
                    </p>
                    <button
                        onClick={() => setIsEmptyState(false)}
                        className="btn btn-primary btn-lg mt-4"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Create Warehouse
                    </button>
                </div>
            </div>
        );
    }

    // MAIN FORM VIEW (Edit or Create Flow)
    return (
        <div className="p-6 max-w-5xl mx-auto space-y-8 animate-fade-in overflow-y-auto h-full pb-20">
            {/* Page Header */}
            <div className="flex flex-col gap-1 pl-12 md:pl-0">
                <h1 className="text-3xl font-bold tracking-tight text-foreground">
                    {mode === 'create' ? 'Create Warehouse' : 'Facilities Profile'}
                </h1>
                <p className="text-muted-foreground">
                    {mode === 'create'
                        ? 'Set up your logistics facility to start managing docks.'
                        : 'Manage your warehouse details, location, and operational settings.'}
                </p>
            </div>

            <form onSubmit={handlePreSubmit} className="space-y-6">
                {/* Profile Card */}
                <div className="card p-6 border-l-4 border-l-primary">
                    <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-primary/10 rounded-lg text-primary">
                            <Building2 size={24} />
                        </div>
                        <h3 className="card-title">General Information</h3>
                    </div>

                    <div className="grid gap-6 md:grid-cols-2">
                        {/* Name Field */}
                        <div>
                            <FormField
                                label="Warehouse Name"
                                value={formData.name}
                                onChange={(val) => handleChange('name', val)}
                                required
                                placeholder="e.g. Abbotsford Distribution Center"
                                disabled={!!initialName} // Name is always locked after first save
                                helpText={!!initialName
                                    ? "Locked for integrity. Contact support to change."
                                    : "Internal name for your facility."
                                }
                            />
                        </div>

                        {/* Warehouse ID Field */}
                        <div>
                            <FormField
                                label="Warehouse ID"
                                value={formData.publicBookingId}
                                onChange={(val) => handleChange('publicBookingId', val)}
                                required
                                placeholder="e.g. abbotsford-dc"
                                disabled={!!initialBookingId} // Locked after first save
                                helpText={!!initialBookingId
                                    ? "Locked. This is the unique code carriers use to schedule."
                                    : "Unique code for carriers. Auto-generated from name."
                                }
                            />
                            {!!initialBookingId && (
                                <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs">
                                    <button
                                        type="button"
                                        className="text-blue-600 dark:text-blue-400 hover:underline font-mono"
                                        onClick={() => {
                                            navigator.clipboard.writeText(formData.publicBookingId);
                                            toast.success("Warehouse ID copied");
                                        }}
                                    >
                                        {formData.publicBookingId} (Copy ID)
                                    </button>
                                    <button
                                        type="button"
                                        className="text-teal-600 dark:text-teal-400 hover:underline font-medium"
                                        onClick={() => {
                                            const url = `${window.location.origin}/carrier-request?warehouse=${encodeURIComponent(formData.publicBookingId)}`;
                                            navigator.clipboard.writeText(url);
                                            toast.success("Shareable carrier booking link copied");
                                        }}
                                    >
                                        Copy Shareable Booking Link
                                    </button>
                                </div>
                            )}
                        </div>

                        <FormField
                            label="Contact Email"
                            type="email"
                            value={formData.email}
                            onChange={(val) => handleChange('email', val)}
                            required
                            placeholder="ops@company.com"
                            helpText="Required for notifications"
                        />
                        <FormField
                            label="Phone Number"
                            type="tel"
                            value={formData.phone}
                            onChange={(val) => handleChange('phone', val)}
                            required
                            placeholder="+1 (555) 000-0000"
                            helpText="Minimum 10 digits"
                            error={fieldErrors.phone}
                        />
                    </div>
                </div>

                {/* Location Card */}
                <div className="card p-6 border-l-4 border-l-blue-500">
                    <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-blue-500/10 rounded-lg text-blue-500">
                            <MapPin size={24} />
                        </div>
                        <h3 className="card-title">Location & Address</h3>
                    </div>

                    <div className="grid gap-6">
                        <FormField
                            label="Street Address"
                            value={formData.address}
                            onChange={(val) => handleChange('address', val)}
                            required
                            placeholder="123 Logistics Way"
                        />

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-start">
                            <div className="col-span-2 md:col-span-1">
                                <FormField
                                    label="City"
                                    value={formData.city}
                                    onChange={(val) => handleChange('city', val)}
                                    required
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                {formData.country === 'Canada' ? (
                                    <div className="form-group">
                                        <label className="label">Province <span className="text-red-500">*</span></label>
                                        <select
                                            className="field"
                                            value={formData.state}
                                            onChange={(e) => handleChange('state', e.target.value)}
                                            required
                                        >
                                            <option value="">Select province...</option>
                                            {CANADIAN_PROVINCES.map(p => (
                                                <option key={p.value} value={p.value}>{p.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                ) : formData.country === 'USA' ? (
                                    <div className="form-group">
                                        <label className="label">State <span className="text-red-500">*</span></label>
                                        <select
                                            className="field"
                                            value={formData.state}
                                            onChange={(e) => handleChange('state', e.target.value)}
                                            required
                                        >
                                            <option value="">Select state...</option>
                                            {US_STATES.map(s => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    </div>
                                ) : (
                                    <FormField
                                        label="State / Province"
                                        value={formData.state}
                                        onChange={(val) => handleChange('state', val)}
                                        required
                                        placeholder="State / Province"
                                    />
                                )}
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <FormField
                                    label="ZIP / Postal Code"
                                    value={formData.zipCode}
                                    onChange={(val) => handleChange('zipCode', val)}
                                    required
                                    placeholder="V2S 1A1"
                                    error={fieldErrors.zipCode}
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <div className="form-group">
                                    <label className="label">Country <span className="text-red-500">*</span></label>
                                    <select
                                        className={`field ${fieldErrors.country ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                                        value={formData.country}
                                        onChange={(e) => handleChange('country', e.target.value)}
                                    >
                                        {ALLOWED_COUNTRIES.map(c => (
                                            <option key={c.value} value={c.value}>{c.label}</option>
                                        ))}
                                    </select>
                                    {fieldErrors.country && <p className="text-xs text-red-500 mt-1">{fieldErrors.country}</p>}
                                </div>
                            </div>
                        </div>

                        <p className="text-[12px] text-muted-foreground -mt-2">
                            Use 2-letter state/province code (e.g. CA, BC). ZIP/Postal must be at least 5 characters.
                        </p>

                        <div className="w-full h-px bg-slate-200 dark:bg-slate-800 my-2" />

                        <div className="grid md:grid-cols-2 gap-6">
                            <FormField
                                label="Latitude"
                                value={formData.latitude}
                                onChange={(val) => handleChange('latitude', val)}
                                placeholder="49.0000"
                                helpText="Optional. Used for weather and proximity features."
                            />
                            <FormField
                                label="Longitude"
                                value={formData.longitude}
                                onChange={(val) => handleChange('longitude', val)}
                                placeholder="-122.0000"
                                helpText="Optional. Used for weather and proximity features."
                            />
                        </div>
                    </div>
                </div>

                {/* Operations Card */}
                <div className="card p-6 border-l-4 border-l-purple-500">
                    <div className="flex items-center gap-2 mb-6">
                        <div className="p-2 bg-purple-500/10 rounded-lg text-purple-500">
                            <SettingsIcon size={24} />
                        </div>
                        <h3 className="card-title">Operational Settings</h3>
                    </div>

                    <div className="grid gap-6 md:grid-cols-3">
                        <div className="form-group">
                            <label className="label">Time Zone</label>
                            <select
                                className="field"
                                name="timeZone"
                                value={formData.timeZone}
                                onChange={(e) => handleChange('timeZone', e.target.value)}
                            >
                                {SUPPORTED_TIMEZONES.map(tz => (
                                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                                ))}
                            </select>
                            <p className="text-xs text-muted-foreground mt-1">Used for local appointment times.</p>
                        </div>

                        <FormField
                            label="Total Docks"
                            type="number"
                            value={String(formData.dockCount)}
                            onChange={(val) => handleChange('dockCount', val)}
                            helpText="Number of loading docks at this facility."
                        />

                        <FormField
                            label="Late Arrival Threshold (Mins)"
                            type="number"
                            value={String(formData.graceMinutes)}
                            onChange={(val) => handleChange('graceMinutes', val)}
                            helpText="A truck is marked late if it checks in after this many minutes past the appointment time."
                        />
                    </div>
                </div>

                {/* Operating Hours (only in edit mode) */}
                {
                    mode === 'edit' && warehouseId && (
                        <WarehouseScheduleSettings
                            warehouseId={warehouseId}
                            initialSchedule={{ ...warehouseSchedule, timeZone: formData.timeZone }}
                            hideTimezoneSelector={true}
                            onTimeZoneChange={(tz) => handleChange('timeZone', tz)}
                            onSaved={() => loadWarehouseData()}
                        />
                    )
                }

                {/* Footer Actions */}
                <div className="flex items-center justify-end gap-3 pt-4">
                    <button
                        type="button"
                        className="btn btn-outline"
                        onClick={handleReset}
                        disabled={saving}
                    >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reset
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={saving}
                    >
                        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {mode === 'create' ? 'Create Warehouse' : 'Save Changes'}
                        {!saving && <Save className="w-4 h-4 ml-2" />}
                    </button>
                </div>
            </form >

            {/* Name Confirmation Alert Dialog */}
            < AlertDialog.Root open={showNameConfirm} onOpenChange={setShowNameConfirm} >
                <AlertDialog.Portal>
                    <AlertDialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-fade-in" />
                    <AlertDialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-slate-900 p-6 rounded-xl shadow-2xl z-50 w-full max-w-md border border-slate-200 dark:border-slate-800 animate-scale-in">
                        <div className="flex flex-col gap-4">
                            <div className="flex items-center gap-3 text-amber-500">
                                <AlertTriangle className="w-8 h-8" />
                                <AlertDialog.Title className="text-xl font-bold text-foreground">
                                    Confirm Carrier Booking Name
                                </AlertDialog.Title>
                            </div>

                            <AlertDialog.Description className="text-muted-foreground">
                                <p className="mb-3">
                                    You are setting the warehouse name to: <br />
                                    <strong>"{formData.name}"</strong>
                                </p>
                                <ul className="list-disc pl-5 space-y-1 text-sm">
                                    <li>This is the <strong>exact wording</strong> carriers must enter to schedule appointments.</li>
                                    <li>After saving, it <strong>cannot be changed</strong> without BayWhiz support.</li>
                                </ul>
                            </AlertDialog.Description>

                            <div className="flex justify-end gap-3 mt-4">
                                <AlertDialog.Cancel asChild>
                                    <button className="btn btn-outline">Cancel</button>
                                </AlertDialog.Cancel>
                                <AlertDialog.Action asChild>
                                    <button
                                        onClick={executeSubmit}
                                        className="btn btn-primary bg-amber-500 hover:bg-amber-600 border-amber-600 text-white"
                                    >
                                        Confirm & Save
                                    </button>
                                </AlertDialog.Action>
                            </div>
                        </div>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root >
        </div >
    );
}
